'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Copy, Loader2, MinusCircle, PlusCircle, Ticket, Wallet, XCircle } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useOrgSlug } from '@/hooks/use-org-slug';
import { trpc } from '@/lib/api/trpc';

function formatUsd(cents: number): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

/** Parse a dollars input ("25" or "25.50") into integer cents, or null if invalid. */
function parseDollarsToCents(raw: string): number | null {
  const trimmed = raw.trim().replace(',', '.');
  if (!/^-?\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const cents = Math.round(Number.parseFloat(trimmed) * 100);
  return Number.isSafeInteger(cents) ? cents : null;
}

interface BillingCreditsTabProps {
  accountId: string | undefined;
}

export function BillingCreditsTab({ accountId }: BillingCreditsTabProps) {
  const { t } = useTranslation('common');

  if (!accountId) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          {t('accountManager.billingCredits.loadingAccount')}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <BalanceSection accountId={accountId} />
      <GrantSection accountId={accountId} />
      <PromoCodesSection accountId={accountId} />
      <LedgerSection accountId={accountId} />
    </div>
  );
}

function BalanceSection({ accountId }: { accountId: string }) {
  const { t } = useTranslation('common');
  const balanceQuery = trpc.billingCredits.adminGetBalance.useQuery({ accountId });
  const data = balanceQuery.data;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wallet className="h-5 w-5" />
          {t('accountManager.billingCredits.title')}
        </CardTitle>
        <CardDescription className="max-w-2xl">
          {t('accountManager.billingCredits.description')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {balanceQuery.isLoading || !data ? (
          <Skeleton className="h-20 w-full" />
        ) : (
          <div className="grid gap-4 sm:grid-cols-3">
            <SummaryStat
              label={t('accountManager.billingCredits.balance')}
              value={formatUsd(data.balanceCents)}
              highlight
            />
            <SummaryStat
              label={t('accountManager.billingCredits.lifetimeGranted')}
              value={formatUsd(data.lifetimeGrantedCents)}
            />
            <SummaryStat
              label={t('accountManager.billingCredits.lifetimeApplied')}
              value={formatUsd(data.lifetimeAppliedCents)}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function GrantSection({ accountId }: { accountId: string }) {
  const { t } = useTranslation('common');
  const utils = trpc.useUtils();

  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  const grantMutation = trpc.billingCredits.adminGrant.useMutation({
    onSuccess: () => {
      toast.success(t('accountManager.billingCredits.toastGrantSuccess'));
      setAmount('');
      setReason('');
      void utils.billingCredits.adminGetBalance.invalidate({ accountId });
      void utils.billingCredits.adminListLedger.invalidate({ accountId });
    },
    onError: (error) => {
      toast.error(error.message || t('accountManager.billingCredits.toastGrantError'));
    },
  });

  const amountCents = parseDollarsToCents(amount);
  const isAmountValid = amountCents !== null && amountCents !== 0;
  const isReasonValid = reason.trim().length > 0;
  const canSubmit = isAmountValid && isReasonValid && !grantMutation.isPending;

  const handleSubmit = () => {
    if (!isAmountValid || !isReasonValid || amountCents === null) return;
    grantMutation.mutate({ accountId, amountCents, reason: reason.trim() });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('accountManager.billingCredits.adjustTitle')}</CardTitle>
        <CardDescription>{t('accountManager.billingCredits.adjustDescription')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="billing-credits-amount">
            {t('accountManager.billingCredits.amountLabel')}
          </Label>
          <Input
            id="billing-credits-amount"
            inputMode="decimal"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            placeholder={t('accountManager.billingCredits.amountPlaceholder')}
            disabled={grantMutation.isPending}
            className="max-w-xs"
          />
          <p className="text-xs text-muted-foreground">
            {t('accountManager.billingCredits.amountHint')}
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="billing-credits-reason">
            {t('accountManager.billingCredits.reasonLabel')}
          </Label>
          <Textarea
            id="billing-credits-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={t('accountManager.billingCredits.reasonPlaceholder')}
            rows={2}
            maxLength={200}
            disabled={grantMutation.isPending}
          />
        </div>
        <Button onClick={handleSubmit} disabled={!canSubmit}>
          {grantMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('accountManager.billingCredits.submitting')}
            </>
          ) : isAmountValid && amountCents !== null && amountCents < 0 ? (
            <>
              <MinusCircle className="mr-2 h-4 w-4" />
              {t('accountManager.billingCredits.clawbackButton')}
            </>
          ) : (
            <>
              <PlusCircle className="mr-2 h-4 w-4" />
              {t('accountManager.billingCredits.grantButton')}
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}

function PromoCodesSection({ accountId }: { accountId: string }) {
  const { t } = useTranslation('common');
  const utils = trpc.useUtils();
  const { activeOrgSlug } = useOrgSlug();

  const codesQuery = trpc.billingCredits.listPromoCodes.useQuery({ accountId, limit: 100 });

  const [createAmount, setCreateAmount] = useState('');
  const [createExpiry, setCreateExpiry] = useState('');
  const [createTag, setCreateTag] = useState('');

  const createMutation = trpc.billingCredits.createPromoCodes.useMutation({
    onSuccess: (result) => {
      const created = result?.codes?.[0];
      toast.success(
        t('accountManager.billingCredits.toastCreateSuccess', { code: created?.code ?? '' }),
      );
      setCreateAmount('');
      setCreateExpiry('');
      setCreateTag('');
      void utils.billingCredits.listPromoCodes.invalidate({ accountId, limit: 100 });
    },
    onError: (error) => {
      toast.error(error.message || t('accountManager.billingCredits.toastCreateError'));
    },
  });

  const disableMutation = trpc.billingCredits.disablePromoCode.useMutation({
    onSuccess: () => {
      toast.success(t('accountManager.billingCredits.toastDisableSuccess'));
      void utils.billingCredits.listPromoCodes.invalidate({ accountId, limit: 100 });
    },
    onError: (error) => {
      toast.error(error.message || t('accountManager.billingCredits.toastDisableError'));
    },
  });

  const createAmountCents = parseDollarsToCents(createAmount);
  const isCreateAmountValid = createAmountCents !== null && createAmountCents > 0;
  const canCreate = isCreateAmountValid && !createMutation.isPending;

  const handleCreate = () => {
    if (!isCreateAmountValid || createAmountCents === null) return;
    createMutation.mutate({
      amountCents: createAmountCents,
      accountIds: [accountId],
      expiresAt: createExpiry ? new Date(`${createExpiry}T23:59:59Z`).toISOString() : null,
      campaignTag: createTag.trim() || null,
    });
  };

  const handleCopyLink = (code: string) => {
    if (!activeOrgSlug) return;
    const link = `${window.location.origin}/org/${activeOrgSlug}/billing?tab=credits&code=${encodeURIComponent(code)}`;
    void navigator.clipboard.writeText(link);
    toast.success(t('accountManager.billingCredits.toastLinkCopied'));
  };

  const codes = codesQuery.data?.codes ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Ticket className="h-5 w-5" />
          {t('accountManager.billingCredits.promoCodesTitle')}
        </CardTitle>
        <CardDescription>{t('accountManager.billingCredits.promoCodesDescription')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-2">
            <Label htmlFor="promo-create-amount">
              {t('accountManager.billingCredits.createAmountLabel')}
            </Label>
            <Input
              id="promo-create-amount"
              inputMode="decimal"
              value={createAmount}
              onChange={(event) => setCreateAmount(event.target.value)}
              placeholder="100"
              disabled={createMutation.isPending}
              className="w-32"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="promo-create-expiry">
              {t('accountManager.billingCredits.createExpiryLabel')}
            </Label>
            <Input
              id="promo-create-expiry"
              type="date"
              value={createExpiry}
              onChange={(event) => setCreateExpiry(event.target.value)}
              disabled={createMutation.isPending}
              className="w-44"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="promo-create-tag">
              {t('accountManager.billingCredits.createTagLabel')}
            </Label>
            <Input
              id="promo-create-tag"
              value={createTag}
              onChange={(event) => setCreateTag(event.target.value)}
              placeholder={t('accountManager.billingCredits.createTagPlaceholder')}
              maxLength={100}
              disabled={createMutation.isPending}
              className="w-52"
            />
          </div>
          <Button onClick={handleCreate} disabled={!canCreate}>
            {createMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <PlusCircle className="mr-2 h-4 w-4" />
            )}
            {t('accountManager.billingCredits.createButton')}
          </Button>
        </div>

        {codesQuery.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : codes.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('accountManager.billingCredits.noCodes')}
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('accountManager.billingCredits.table.code')}</TableHead>
                <TableHead>{t('accountManager.billingCredits.table.amount')}</TableHead>
                <TableHead>{t('accountManager.billingCredits.table.status')}</TableHead>
                <TableHead>{t('accountManager.billingCredits.table.redemptions')}</TableHead>
                <TableHead>{t('accountManager.billingCredits.table.expiry')}</TableHead>
                <TableHead>{t('accountManager.billingCredits.table.campaign')}</TableHead>
                <TableHead className="text-right">
                  {t('accountManager.billingCredits.table.actions')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {codes.map((code) => {
                const isExpired = code.expiresAt && new Date(code.expiresAt) < new Date();
                const status =
                  code.status !== 'active' ? code.status : isExpired ? 'expired' : 'active';
                return (
                  <TableRow key={code.id}>
                    <TableCell className="font-mono text-sm">{code.code}</TableCell>
                    <TableCell>{formatUsd(code.amountCents)}</TableCell>
                    <TableCell>
                      <Badge variant={status === 'active' ? 'default' : 'secondary'}>
                        {t(`accountManager.billingCredits.status.${status}`, {
                          defaultValue: status,
                        })}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {code.redemptionCount}/{code.maxRedemptions}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {code.expiresAt ? new Date(code.expiresAt).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {code.campaignTag ?? '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopyLink(code.code)}
                          title={t('accountManager.billingCredits.copyLink')}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        {status === 'active' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => disableMutation.mutate({ promoCodeId: code.id })}
                            disabled={disableMutation.isPending}
                            title={t('accountManager.billingCredits.disableCode')}
                          >
                            <XCircle className="h-4 w-4 text-red-600" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function LedgerSection({ accountId }: { accountId: string }) {
  const { t } = useTranslation('common');
  const ledgerQuery = trpc.billingCredits.adminListLedger.useQuery({ accountId, limit: 25 });
  const entries = ledgerQuery.data?.entries ?? [];

  if (ledgerQuery.isLoading || entries.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('accountManager.billingCredits.historyTitle')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="divide-y">
          {entries.map((entry) => {
            const isPositive = entry.amountCents >= 0;
            return (
              <div key={entry.id} className="flex items-center justify-between gap-4 py-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">
                      {t(`accountManager.billingCredits.ledgerType.${entry.type}`, {
                        defaultValue: entry.type,
                      })}
                    </Badge>
                    {entry.description && (
                      <span className="truncate text-sm">{entry.description}</span>
                    )}
                  </div>
                  {entry.createdAt && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {new Date(entry.createdAt).toLocaleString()}
                    </p>
                  )}
                </div>
                <div className="shrink-0 text-right">
                  <p className={isPositive ? 'font-medium text-emerald-600' : 'font-medium text-red-600'}>
                    {isPositive ? '+' : ''}
                    {formatUsd(entry.amountCents)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatUsd(entry.balanceAfterCents)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function SummaryStat({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="rounded-lg border p-4">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className={highlight ? 'mt-1 text-2xl font-semibold' : 'mt-1 text-2xl font-medium'}>
        {value}
      </p>
    </div>
  );
}
